import React, { useCallback, useMemo, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import Animated, { FadeIn, FadeOut, LayoutAnimationConfig } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Navbar, theme } from '@sg/garnish';

import { ORDER_AGAIN_TABS, OrderAgain } from '@order/features-orders';
import { useTelemetry } from '@order/Telemetry';

import { NavbarBagButton, SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { type useReorderOrderLineItem, useCart, useIsLiquidGlassEnabled } from '../../hooks';
import { ReorderLocationHeader } from './components';
import {
  type ReorderModalDeliveryContextQuery,
  useReorderModalDeliveryContextQuery,
} from './graphql/ReorderModalDeliveryContext.generated';
import { useReorderModalLocation, useReorderOrders } from './hooks';
import { useReorderOrdersV2 } from './hooks/useReorderOrdersV2';
import { ReorderScreenFavoritesTabPanel, ReorderScreenOrdersTabPanel } from './tabs';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A modal-screen that provides a simple user interface to reorder previous
 * orders and favorites.
 */
export const ReorderModal = () => {
  const router = useRouter();
  const { restaurantId, addressId } = useLocalSearchParams<{
    restaurantId?: string;
    addressId?: string;
  }>();

  const [{ data: deliveryContext }] = useReorderModalDeliveryContextQuery({
    variables: { input: { addressId } },
    requestPolicy: 'cache-first',
    pause: !addressId,
  });

  const explicitRestaurantId = restaurantId ? Number.parseInt(restaurantId, 10) : undefined;

  const targetRestaurant = useMemo(
    () => buildDeliveryTargetRestaurant(addressId, deliveryContext),
    [addressId, deliveryContext],
  );

  const onBackHandler = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.dismissTo('/');
  }, [router]);

  return (
    <TransparentModal onClose={onBackHandler} size="small" dialogMaxHeight={844}>
      <ReorderScreenContent
        canCloseScreen={true}
        showBagButtonV2={false}
        shouldShowLocation={true}
        onBackHandler={onBackHandler}
        explicitRestaurantId={explicitRestaurantId}
        explicitAddressId={addressId}
        targetRestaurant={targetRestaurant}
      />
    </TransparentModal>
  );
};

/**
 * A tab-screen that provides a simple user interface to reorder previous
 * orders and favorites.
 */
export const ReorderTab = () => {
  return (
    <ReorderScreenContent
      canCloseScreen={false}
      showBagButtonV2={true}
      shouldShowLocation={false}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ReorderScreenContent = (props: ReorderScreenReadyProps) => {
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn) {
    return <SignedOutView withoutFooter />;
  }

  return <ReorderScreenReady {...props} />;
};

const ReorderScreenReady = (props: ReorderScreenReadyProps) => {
  const {
    canCloseScreen,
    showBagButtonV2,
    shouldShowLocation,
    onBackHandler,
    explicitRestaurantId,
    explicitAddressId,
    targetRestaurant,
  } = props;

  const { lineItemsCount } = useCart();
  const { track } = useTelemetry();

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // Fall back to the last interacted store when no explicit restaurant was passed.
  const lastInteractedStore = useLastInteractedStore();

  const fallbackRestaurantId = lastInteractedStore?.restaurantId
    ? Number.parseInt(lastInteractedStore.restaurantId, 10)
    : undefined;

  const currentRestaurantId = explicitRestaurantId ?? fallbackRestaurantId;
  const currentAddressId = explicitRestaurantId
    ? explicitAddressId
    : lastInteractedStore?.addressId;

  // ─── Remote Data ─────────────────────────────────────────────────────

  // Always call both hooks to follow React rules, then conditionally use the result

  const reorderOrdersV1 = useReorderOrders();
  const reorderOrdersV2 = useReorderOrdersV2(currentRestaurantId);

  const reorderOrders = currentRestaurantId ? reorderOrdersV2 : reorderOrdersV1;

  const reorderLocation = useReorderModalLocation({
    restaurantId: shouldShowLocation ? currentRestaurantId : undefined,
    addressId: shouldShowLocation ? currentAddressId : undefined,
  });

  // ─── State ───────────────────────────────────────────────────────────

  const [activeTabId, setActiveTabId] = useState(ORDER_AGAIN_TABS.ORDERS.ID);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onTabChange = useCallback(
    (tabId: string) => {
      if (tabId === ORDER_AGAIN_TABS.ORDERS.ID) {
        track('reorder.orders.tab.tapped');
      }

      if (tabId === ORDER_AGAIN_TABS.FAVORITES.ID) {
        track('reorder.favorites.tab.tapped');
      }

      setActiveTabId(tabId);
    },
    [track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <LayoutAnimationConfig skipEntering>
        {reorderLocation && onBackHandler ? (
          <Animated.View key="location" entering={HEADER_ENTERING} exiting={HEADER_EXITING}>
            <ReorderLocationHeader
              orderChannel={reorderLocation.orderChannel}
              locationName={reorderLocation.locationName}
              onClose={onBackHandler}
            />
          </Animated.View>
        ) : (
          <Animated.View key="title" entering={HEADER_ENTERING} exiting={HEADER_EXITING}>
            <OrderAgain.Header shouldUseSafeArea={false}>
              <OrderAgain.HeaderTitle />

              {canCloseScreen && onBackHandler ? (
                <OrderAgain.CloseButton onPress={onBackHandler} />
              ) : null}
            </OrderAgain.Header>
          </Animated.View>
        )}
      </LayoutAnimationConfig>

      <OrderAgain.Tabs activeTabId={activeTabId} setActiveTabId={onTabChange} />

      <ReorderScreenOrdersTabPanel
        activeTabId={activeTabId}
        reorderOrders={reorderOrders}
        targetRestaurant={targetRestaurant}
      />

      <ReorderScreenFavoritesTabPanel
        activeTabId={activeTabId}
        mostRecentOrder={reorderOrders.mostRecentOrder}
        restaurantId={currentRestaurantId}
        targetRestaurant={targetRestaurant}
      />

      {lineItemsCount > 0 && !showBagButtonV2 ? (
        <Navbar.Container isLiquidGlassEnabled={isLiquidGlassEnabled}>
          <View style={styles.navbarButtonWrapper}>
            <NavbarBagButton isLiquidGlassEnabled={isLiquidGlassEnabled} />
          </View>
        </Navbar.Container>
      ) : null}
    </SafeAreaView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildDeliveryTargetRestaurant(
  addressId: string | undefined,
  deliveryContext: ReorderModalDeliveryContextQuery | undefined,
): TargetRestaurant | undefined {
  if (!addressId) return;

  const location = deliveryContext?.locationByAddress;

  if (location?.__typename !== 'Location') return;

  const { restaurant, vendor } = location;

  return {
    restaurantId: restaurant.id,
    restaurantName: restaurant.name,
    restaurantSlug: restaurant.slug,
    deliveryOrderDetails: {
      tip: 0,
      deliveryFee: restaurant.deliveryFee,
      vendor: vendor.name,
      vendorRestaurantId: vendor.restaurantId,
      address: { id: addressId },
    },
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER_ENTERING = FadeIn.duration(theme.transitions.short);
const HEADER_EXITING = FadeOut.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  navbarButtonWrapper: {
    marginLeft: 'auto',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderScreenReadyProps = {
  canCloseScreen: boolean;
  showBagButtonV2: boolean;
  shouldShowLocation: boolean;
  onBackHandler?: () => void;
  explicitRestaurantId?: number;
  explicitAddressId?: string;
  targetRestaurant?: TargetRestaurant;
};

type TargetRestaurant = NonNullable<
  Parameters<typeof useReorderOrderLineItem>[0]['targetRestaurant']
>;
